<template>
    <div
        class="search-modal-inner"
        :class="props.isEmbedded ? 'embedded-inner' : 'modal-inner'"
        ref="searchModalInner"
    >
        <input-group class="search-input">
            <input-group-addon>
                <font-awesome-icon :icon="['fad', 'search']" />
            </input-group-addon>
            <input-text
                v-model="searchString"
                placeholder="Search platform..."
                ref="searchInput"
                @update:model-value="updateSearchStringProp"
            />
        </input-group>
        <transition
            :name="props.isEmbedded ? 'fade-slide' : ''"
            appear
            mode="out-in"
        >
            <template v-if="!props.isEmbedded || props.isFocused">
                <div v-if="results.length > 0 && searchString.length > 0" class="search-results">
                    <div class="search-results-left-column">
                        <button
                            type="button"
                            class="search-results-left-column-button"
                            v-for="result in results"
                            :key="result.title"
                            @click="setActiveTab(result.title)"
                            :class="{
                                'search-results-left-column-button-active': activeTab === result.title,
                                'focused-element': focusedElement === result.title,
                            }"
                        >
                            {{ result.title }}
                            <div v-if="result.title === 'Research' && fetchingEmbeddedSearchDocumentsResults">
                                <component-loader size="small" />
                            </div>
                            <div v-else class="result-count">{{ result.resultCount }}{{ result.optionHolder.capped ? '+' : '' }}</div>
                            <span
                                class="pin-icon"
                                :class="{ pinned: pinnedCategoryIds.includes(result.title) }"
                                role="button"
                                tabindex="0"
                                title="Pin category"
                                v-tooltip="pinnedCategoryIds.includes(result.title) ? 'Unpin category' : 'Pin category to top'"
                                @click.stop="pinCategory(result)"
                                @keydown.enter.stop.prevent="pinCategory(result)"
                                @keydown.space.stop.prevent="pinCategory(result)"
                            >
                                <i class="fa-solid fa-thumbtack" />
                            </span>
                        </button>
                    </div>
                    <div class="search-results-right-column">
                        <div
                            v-for="result in results"
                            :key="result.title"
                            v-show="activeTab === result.title"
                            class="search-results-right-column-inner"
                            ref="resultsScrollContainer"
                        >
                            <div v-if="fetchingEmbeddedSearchDocumentsResults && result.title === 'Research' && result.optionHolder.options.length === 0" class="loading-results">
                                <component-loader />
                            </div>
                            <template v-else>
                                <div v-if="result.title === 'Research'" class="research-results-toolbar">
                                    <research-selection-control
                                        :model-value="activeResearchSelection"
                                        class="research-scope-toggle"
                                        @update:model-value="updateResearchSearchSelection"
                                    />
                                    <prime-button
                                        class="plain-btn"
                                        @click="viewAllForResult"
                                    >
                                        View all
                                        <font-awesome-icon :icon="['fas', 'arrow-right']" class="view-all-documents-icon" />
                                    </prime-button>
                                </div>
                                <div v-if="result.optionHolder.options.length === 0" class="no-results">
                                    No Results found
                                </div>
                                <table v-else>
                                    <tr
                                        v-for="option in sortedOptions(result)"
                                        :key="option.id"
                                        class="search-result-option"
                                        :class="{
                                            'focused-element': focusedElement === option.id,
                                            'search-result-blocked': option.isLocked && result.blockClickingLockedItems,
                                        }"
                                        @click="selectRow(result, option)"
                                        tabIndex="0"
                                    >
                                        <td>
                                            <country-flag
                                                v-if="result.useCountryIcon"
                                                :iso-code="option.icon as string || ''"
                                                class="search-result-option-country-flag"
                                            />
                                            <icofont-icon
                                                v-else-if="typeof option.icon === 'string'"
                                                background-colour="transparent"
                                                :icon-colour="option.colour ?? 'var(--lcpBlue)'"
                                                :icon="option.icon ?? 'light-bulb'"
                                            />
                                            <font-awesome-icon
                                                v-else-if="Array.isArray(option.icon)"
                                                :icon="option.icon"
                                                class="search-result-option-icon"
                                                :style="{ color: option.colour || 'inherit' }"
                                            />
                                        </td>
                                        <td v-if="result.showId">{{ option.id }}</td>
                                        <td>
                                            {{ option.title }}
                                        </td>
                                        <td v-if="option.extraInfo" class="search-result-extra-info">
                                            <div v-if="typeof option.extraInfo === 'string'">
                                                {{ option.extraInfo }}
                                            </div>
                                            <div v-else-if="Array.isArray(option.extraInfo)">
                                                {{ option.extraInfo.join(' > ') }}
                                            </div>
                                        </td>
                                        <td v-if="result.showOwner">
                                            {{ option.owner }}
                                        </td>
                                        <td class="locked-icon">
                                            <font-awesome-icon
                                                v-if="option.isLocked"
                                                :icon="['fas', 'lock']"
                                                class="search-result-locked-icon"
                                                :title="'Locked: ' + option.title"
                                                v-tooltip="getLockedTooltip(option, result)"
                                            />
                                        </td>
                                        <td>
                                            <span
                                                class="pin-icon"
                                                :class="{ pinned: pinnedSearchIds.includes(option.id) }"
                                                role="button"
                                                tabindex="0"
                                                title="Pin search result"
                                                v-tooltip="pinnedSearchIds.includes(option.id) ? 'Unpin search result' : 'Pin search result'"
                                                @click.stop="pinSearchResult(option, result.title, result.useCountryIcon)"
                                                @keydown.enter.stop.prevent="pinSearchResult(option, result.title, result.useCountryIcon)"
                                                @keydown.space.stop.prevent="pinSearchResult(option, result.title, result.useCountryIcon)"
                                            >
                                                <i class="fa-solid fa-thumbtack" />
                                            </span>
                                        </td>
                                    </tr>
                                </table>
                            </template>
                        </div>
                    </div>
                </div>
                <div v-else-if="searchString.length === 0" class="search-info">
                    <span>Search our views, assets, series and research</span>
                    <div v-if="searchStore.pinnedSearches.length > 0" class="recent-searches">
                        <div class="recent-searches-title">Pinned searches</div>
                        <div class="recent-search-container" ref="pinnedSearchScrollContainer">
                            <div
                                v-for="pinnedSearch in searchStore.pinnedSearches"
                                :key="pinnedSearch.tab + pinnedSearch.option.id"
                                class="recent-searches-item"
                                :class="{
                                    'focused-element': focusedElement === pinnedSearch.option.id,
                                }"
                                :data-search-item-id="pinnedSearch.option.id"
                            >
                                <button
                                    type="button"
                                    @click="selectRecentSearch(pinnedSearch)"
                                >
                                    <span
                                        class="pin-icon"
                                        :class="{ pinned: pinnedSearchIds.includes(pinnedSearch.option.id) }"
                                        role="button"
                                        tabindex="-1"
                                        title="Unpin search result"
                                        v-tooltip="'Unpin search result'"
                                        @click.stop="pinSearchResult(pinnedSearch.option, pinnedSearch.tab, false)"
                                        @keydown.enter.stop.prevent="pinSearchResult(pinnedSearch.option, pinnedSearch.tab, false)"
                                        @keydown.space.stop.prevent="pinSearchResult(pinnedSearch.option, pinnedSearch.tab, false)"
                                    >
                                        <i class="fa-solid fa-thumbtack" />
                                    </span>
                                    <country-flag
                                        v-if="pinnedSearch.useCountryIcon"
                                        :iso-code="pinnedSearch.option.icon as string || ''"
                                        class="search-result-option-country-flag"
                                    />
                                    <icofont-icon
                                        v-else-if="typeof pinnedSearch.option.icon === 'string'"
                                        background-colour="transparent"
                                        :icon-colour="pinnedSearch.option.colour ?? 'var(--lcpBlue)'"
                                        :icon="pinnedSearch.option.icon ?? 'light-bulb'"
                                    />
                                    <font-awesome-icon
                                        v-else-if="Array.isArray(pinnedSearch.option.icon)"
                                        :icon="pinnedSearch.option.icon"
                                        class="search-result-option-icon"
                                        :style="{ color: pinnedSearch.option.colour || 'inherit' }"
                                    />
                                    <div v-else />
                                    <div class="recent-search-text">{{ pinnedSearch.option.title }}</div>
                                    <div class="recent-search-tab">{{ pinnedSearch.tab }}</div>
                                </button>
                            </div>
                        </div>
                    </div>
                    <div v-if="filteredRecentSearches.length > 0" class="recent-searches">
                        <div class="recent-searches-title">Recent searches</div>
                        <div class="recent-search-container" ref="recentSearchScrollContainer">
                            <div
                                v-for="recentSearch in filteredRecentSearches"
                                :key="recentSearch.tab + recentSearch.option.id"
                                class="recent-searches-item"
                                :class="{
                                    'focused-element': focusedElement === recentSearch.option.id,
                                }"
                                :data-search-item-id="recentSearch.option.id"
                            >
                                <button
                                    type="button"
                                    @click="selectRecentSearch(recentSearch)"
                                >
                                    <span
                                        class="pin-icon"
                                        :class="{ pinned: pinnedSearchIds.includes(recentSearch.option.id) }"
                                        role="button"
                                        tabindex="-1"
                                        title="Pin search result"
                                        v-tooltip="'Pin search result'"
                                        @click.stop="pinSearchResult(recentSearch.option, recentSearch.tab, false)"
                                        @keydown.enter.stop.prevent="pinSearchResult(recentSearch.option, recentSearch.tab, false)"
                                        @keydown.space.stop.prevent="pinSearchResult(recentSearch.option, recentSearch.tab, false)"
                                    >
                                        <i class="fa-solid fa-thumbtack" />
                                    </span>
                                    <country-flag
                                        v-if="recentSearch.useCountryIcon"
                                        :iso-code="recentSearch.option.icon as string || ''"
                                        class="search-result-option-country-flag"
                                    />
                                    <icofont-icon
                                        v-else-if="typeof recentSearch.option.icon === 'string'"
                                        background-colour="transparent"
                                        :icon-colour="recentSearch.option.colour ?? 'var(--lcpBlue)'"
                                        :icon="recentSearch.option.icon ?? 'light-bulb'"
                                    />
                                    <font-awesome-icon
                                        v-else-if="Array.isArray(recentSearch.option.icon)"
                                        :icon="recentSearch.option.icon"
                                        class="search-result-option-icon"
                                        :style="{ color: recentSearch.option.colour || 'inherit' }"
                                    />
                                    <div v-else />
                                    <div class="recent-search-text">{{ recentSearch.option.title }}</div>
                                    <div class="recent-search-tab">{{ recentSearch.tab }}</div>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
                <div v-else-if="noResults" class="no-results">No Results found</div>
            </template>
        </transition>
    </div>
</template>

<script lang="ts" setup>

import {
    computed, nextTick, onBeforeUnmount, onMounted, ref,
    watch,
} from 'vue';
import InputText from 'primevue/inputtext';
import InputGroup from 'primevue/inputgroup';
import InputGroupAddon from 'primevue/inputgroupaddon';
import PrimeButton from 'primevue/button';
import { watchDebounced } from '@vueuse/core';
import { authState } from '@lcp/http-auth';
import { RouteRecordRaw, useRoute } from 'vue-router';
import { IFormattedSearchOptions, IFormattedSearchResults, ISearchResults } from '@/interfaces/powerMarkets/ISearchDefinition.ts';
import { IPlantCategoriesByType } from '@/interfaces/powerMarkets/plant/IPlantCategoriesByType.ts';
import { IPlant } from '@/interfaces/powerMarkets/plant/IPlant.ts';
import { useDataStore } from '@/stores/powerMarkets/dataStore.ts';
import { IDefinitionSearchInfo } from '@/interfaces/powerMarkets/static/IDefinitionSearchInfo.ts';
import { useDataStoreActions } from '@/actions/powerMarkets/dataStoreActions.ts';
import { useDefinitionStore } from '@/stores/powerMarkets/definitionStore.ts';
import IcofontIcon from '@/components/powerMarkets/common/IcofontIcon.vue';
import { routes } from '@/routes/routes.ts';
import { enactApiBaseKeyValues } from '@/api/useApi.ts';
import { marketsSidebarInfo } from '@/routes/sidebar/marketsSidebarInfo.ts';
import { assetsSidebarInfo } from '@/routes/sidebar/assetsSidebarInfo.ts';
import { ISidebarRoutesSectionOption, ISidebarRoutesSubSection } from '@/interfaces/routing/ISidebarRoutes.ts';
import { useUserAccessStore } from '@/stores/userAccessStore.ts';
import { IEnactPagesAccess } from '@/interfaces/auth/IEnactPagesAccess.ts';
import { useSearchStore } from '@/stores/searchStore.ts';
import CountryFlag from '@/components/powerMarkets/shared/CountryFlag.vue';
import { useDeltaGptStore } from '@/stores/deltaGpt/deltaGptStore.ts';
import { IRecentSearch } from '@/interfaces/deltaGpt/IRecentSearch.ts';
import ComponentLoader from '@/components/layout/ComponentLoader.vue';
import { router } from '@/router.ts';
import { deltaGptRoutes } from '@/routes/deltaGptRoutes.ts';
import { useDeltaUserSettingsStore } from '@/stores/deltaGpt/deltaGptUserSettingsStore.ts';
import { getReportTypeMetadata, isResearchBrowseDocument, isRichContentDocument } from '@/utils/researchContent.ts';
import { formatPublicationDateForDisplay } from '@/utils/sparkContentUtils.ts';
import { IDocumentSimple } from '@/interfaces/deltaGpt/IDocument.ts';
import { ResearchSelectionType } from '@/interfaces/deltaGpt/IDeltaUserSettings.ts';
import ResearchSelectionControl from '@/components/deltaGpt/ResearchSelectionControl.vue';

const props = defineProps<{
    isEmbedded: boolean;
    searchStringInitial?: string;
    scrollContainer?: HTMLElement | null;
    isFocused?: boolean;
}>();

const dataStore = useDataStore();
const dataStoreActions = useDataStoreActions();
const definitionStore = useDefinitionStore();
const userAccessStore = useUserAccessStore();
const searchStore = useSearchStore();
const deltaGptStore = useDeltaGptStore();
const deltaUserSettingsStore = useDeltaUserSettingsStore();
const currentRoute = useRoute();
const currentRouteIsEnact = computed<boolean>(() => !!currentRoute.matched.find(matchedRoute => matchedRoute.meta.isEnact));

const searchString = ref<string>(props.searchStringInitial ?? '');
const searchDefinitions = ref<Array<IDefinitionSearchInfo>>([]);
const activeTab = ref<string>('');
const focusedElement = ref<string>('');
const previouslyFocusedElement = ref<string>('');
const relatedResearchDocuments = ref<Array<IDocumentSimple> | null>(null);
const guestResearchSearchSelection = ref<ResearchSelectionType>(ResearchSelectionType.PublicProducts);
const resultsScrollContainer = ref<Array<HTMLElement> | null>(null);
const searchModalInner = ref<HTMLElement | null>(null);
const fetchingEmbeddedSearchDocumentsResults = ref<boolean>(false);
const recentSearchScrollContainer = ref<HTMLElement | null>(null);
const pinnedSearchScrollContainer = ref<HTMLElement | null>(null);
const searchInput = ref<InstanceType<typeof InputText> | null>(null);
const pinnedCategoryIds = ref<Array<string>>([...deltaUserSettingsStore.userSettings.pinnedSearchCategories]);
const pinnedSearchIds = ref<Array<string>>([...deltaUserSettingsStore.userSettings.pinnedSearches.map(x => x.option.id)]);
const activeResearchSelection = computed<ResearchSelectionType>(() => (
    authState.value.loggedIn
        ? deltaUserSettingsStore.userSettings.researchSearchSelection ?? ResearchSelectionType.AllProducts
        : guestResearchSearchSelection.value
));
const filteredRecentSearches = computed<Array<IRecentSearch>>(() => {
    const pinned = new Set(pinnedSearchIds.value);
    return searchStore.recentSearches
        .filter(id => !pinned.has(id.option.id));
});

const navigableIdleSearches = computed<Array<IRecentSearch>>(() => (
    [...searchStore.pinnedSearches, ...filteredRecentSearches.value]
));

const emit = defineEmits<{
    'close': [];
    'select': [resultTitle: string, resultId: string];
    'update:searchStringInitial': [searchString: string];
    'update:isFocused': [isFocused: boolean];
}>();

function viewInResearch (): void {
    deltaUserSettingsStore.searchFilter = searchString.value;
    router.push({ name: deltaGptRoutes.documentList.name });
    emit('close');
}

function viewAllForResult (): void {
    viewInResearch();
}

function updateResearchSearchSelection (selection: ResearchSelectionType): void {
    if (authState.value.loggedIn) {
        deltaUserSettingsStore.updateUserSettings({ researchSearchSelection: selection });
    } else {
        guestResearchSearchSelection.value = selection;
    }
    focusedElement.value = '';
    previouslyFocusedElement.value = '';

    const researchResultsContainer = resultsScrollContainer.value?.find(container => container.querySelector('.research-results-toolbar'));
    if (researchResultsContainer) researchResultsContainer.scrollTop = 0;
}

function updateSearchStringProp (value: string | undefined): void {
    emit('update:searchStringInitial', value ?? '');
}

function handleFocusIn (): void {
    emit('update:isFocused', true);

    const el = searchModalInner.value;
    if (!el || !props.scrollContainer) return;

    // Desired offset from viewport top
    const offset = window.innerHeight * 0.25;

    // Get current element position relative to viewport
    const searchRect = el.getBoundingClientRect();
    if (searchRect.top < window.innerHeight * 0.4 && searchRect.top > window.innerHeight * 0.15) return;

    // The scroll amount required
    const scrollY = props.scrollContainer.scrollTop + searchRect.top - offset;

    smoothScrollTo(props.scrollContainer, scrollY, 500);
}

function smoothScrollTo (
    container: HTMLElement,
    targetY: number,
    duration = 500, // milliseconds
): void {
    const startY = container.scrollTop;
    const distance = targetY - startY;
    const startTime = performance.now();

    function easeInOutQuad (t: number): number {
        return t < 0.5
            ? 2 * t * t
            : 1 - (-2 * t + 2) ** 2 / 2;
    }

    function loop (currentTime: number): void {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);

        const easedProgress = easeInOutQuad(progress);
        container.scrollTop = startY + distance * easedProgress;

        if (progress < 1) {
            requestAnimationFrame(loop);
        }
    }

    requestAnimationFrame(loop);
}

function handleFocusOut (event: FocusEvent): void {
    // check if the newly focused element is outside the container
    const container = searchModalInner.value;
    if (!container) return;

    const relatedTarget = event.relatedTarget as HTMLElement | null;

    if (!relatedTarget || !container.contains(relatedTarget)) {
        emit('update:isFocused', false);
    }
}

function pinCategory (row: ISearchResults): void {
    const index = pinnedCategoryIds.value.indexOf(row.title);

    if (index !== -1) {
        pinnedCategoryIds.value.splice(index, 1);
    } else {
        pinnedCategoryIds.value.push(row.title);
    }

    deltaUserSettingsStore.updateUserSettings({ pinnedSearchCategories: pinnedCategoryIds.value });
}

function pinSearchResult (option: IFormattedSearchOptions, title: string, useCountryIcon?: boolean): void {
    const index = pinnedSearchIds.value.indexOf(option.id);

    if (index !== -1) {
        pinnedSearchIds.value.splice(index, 1);
        searchStore.removePinnedSearch(option, title);
    } else {
        pinnedSearchIds.value.push(option.id);
        searchStore.addPinnedSearch(option, title, useCountryIcon ?? false);
    }
}

const sortedOptions = (result: ISearchResults): IFormattedSearchOptions[] => {
    const options = result.optionHolder.options;

    return [...options].sort((a, b) => {
        const aPinned = pinnedSearchIds.value.includes(a.id) ? 0 : 1;
        const bPinned = pinnedSearchIds.value.includes(b.id) ? 0 : 1;

        if (aPinned !== bPinned) return aPinned - bPinned;

        return 1;
    });
};

onMounted(() => {
    const container = searchModalInner.value;
    if (!container) return;

    container.addEventListener('focusin', handleFocusIn);
    container.addEventListener('focusout', handleFocusOut);
});

onBeforeUnmount(() => {
    const container = searchModalInner.value;
    if (!container) return;

    container.removeEventListener('focusin', handleFocusIn);
    container.removeEventListener('focusout', handleFocusOut);
});

const flattenedSidebarInfo = computed<Array<{ icon: Array<string>; route: string }>>(() => {
    const sidebarInfo: Array<{ icon: Array<string>; route: string }> = [];
    const allSidebarInfo: Array<ISidebarRoutesSectionOption | ISidebarRoutesSubSection> = [
        ...marketsSidebarInfo,
        ...assetsSidebarInfo,
    ];

    allSidebarInfo.forEach(info => {
        const asSubsection = info as ISidebarRoutesSubSection;
        const asSection = info as ISidebarRoutesSectionOption;
        if (asSubsection?.pages && asSubsection.pages.length > 0) {
            asSubsection.pages.forEach(page => {
                sidebarInfo.push({
                    icon: page.icon,
                    route: page.route,
                });
            });
        } else {
            sidebarInfo.push({
                icon: asSection.icon,
                route: asSection.route || '',
            });
        }
    });
    return sidebarInfo;
});

const searchablePages = computed<Array<IFormattedSearchOptions>>(() => {
    const flattenedRoutes: Array<RouteRecordRaw & { nameStructure: Array<string> }> = [];
    const addRoutes = (routesToConsider: Array<RouteRecordRaw>, parentNameStructure: Array<string>): void => {
        routesToConsider.forEach(route => {
            const nameStructure = [...parentNameStructure, String(route.meta?.headName)];

            if (!removeFromListIfDevSite(route) && !(route.meta?.searchMeta as { noSearch?: boolean })?.noSearch && !route.path.split('/').some(segment => segment.startsWith(':') && !segment.endsWith('?'))) {
                flattenedRoutes.push({ ...route, nameStructure });
            }
            if (route.children?.length && route.children.length > 0) {
                addRoutes(route.children, nameStructure);
            }
        });
    };

    addRoutes(routes, []);

    const formattedOptions: Array<IFormattedSearchOptions> = flattenedRoutes.map(element => {
        const associatedSidebarInfo = flattenedSidebarInfo.value.find(info => info.route === element.name);
        let searchQuery = element.meta?.headName?.toLowerCase() || (element.meta?.title as string)?.toLowerCase() || '';
        searchQuery += (element.meta?.searchMeta as { tags?: Array<string> })?.tags?.join(' ').toLowerCase() || '';
        return {
            id: typeof element.name === 'string'
                ? element.name : element.path,
            title: element.nameStructure[element.nameStructure.length - 1],
            searchQuery,
            icon: associatedSidebarInfo?.icon,
            extraInfo: element.nameStructure,
            isLocked: !hasPageAccess(element.meta?.accessName),
        };
    });
    return formattedOptions;
});

const storetrackSearchableContent = computed<{ companies: Array<IFormattedSearchOptions>; projects: Array<IFormattedSearchOptions> }>(() => {
    const searchableStoretrackContent = searchStore.storetrackSearchableContent;
    if (!searchableStoretrackContent) {
        return {
            companies: [],
            projects: [],
        };
    }

    const formattedCompanies: Array<IFormattedSearchOptions> = searchableStoretrackContent.companies.map(company => ({
        id: company.id,
        title: company.name,
        searchQuery: company.name.toLowerCase(),
        isLocked: company.companyBlocked,
        icon: ['fas', 'building'],
    }));
    const formattedProjects: Array<IFormattedSearchOptions> = searchableStoretrackContent.projects.map(project => ({
        id: project.factStorageId.toString(),
        title: project.projectName,
        searchQuery: `${project.projectName.toLowerCase()} ${project.technology.join(' ').toLowerCase()}`,
        isLocked: project.projectBlocked,
        icon: project.country.toLowerCase(),
    }));

    return {
        companies: formattedCompanies,
        projects: formattedProjects,
    };
});

function selectRecentSearch (recentSearch: IRecentSearch): void {
    selectSearchResult(recentSearch.tab, recentSearch.option, recentSearch.useCountryIcon);
}

function getLockedTooltip (option: IFormattedSearchOptions, result: ISearchResults): string {
    if (!option.isLocked || !result.blockClickingLockedItems) return '';
    if (!authState.value.loggedIn) return 'You are currently not logged in, please log in to gain access to your subscribed pages.';
    return 'Your current subscription does not grant you access to this content. You can view it, but not select it.';
}

function selectRow (result: ISearchResults, option: IFormattedSearchOptions): void {
    if (option.isLocked && result.blockClickingLockedItems) return;
    selectSearchResult(result.title, option, result.useCountryIcon ?? false);
}

function setActiveTab (title: string): void {
    activeTab.value = title;
    focusedElement.value = '';
}

function removeFromListIfDevSite (route: RouteRecordRaw): boolean {
    const isSiteAdminRoute = (route.meta?.searchMeta as { isSiteAdmin?: boolean })?.isSiteAdmin;
    const isEnactAdminRoute = (route.meta?.searchMeta as { isEnactAdmin?: boolean })?.isEnactAdmin;

    if (isSiteAdminRoute && !userAccessStore.userAccess.isAdmin) {
        return true;
    }

    if (isEnactAdminRoute && !userAccessStore.userAccess.enact.shortTermMarkets?.isAdmin) {
        return true;
    }

    return false;
}

function hasPageAccess (accessRequired: keyof IEnactPagesAccess | undefined): boolean {
    if (!accessRequired || !userAccessStore.enactPageAccess[accessRequired]) return true;
    return userAccessStore.enactPageAccess[accessRequired].hasAccess;
}

// #region Calculate results logic

const noResults = computed<boolean>(() => searchString.value.length > 1 && results.value.every(result => result.optionHolder.options.length === 0));

const results = computed<Array<ISearchResults>>(() => {
    if (!searchString.value?.length || searchString.value.length <= 1) return [];

    const allSearchResults: Array<ISearchResults> = [];

    if (userAccessStore.userAccess.research.isSubscriber || !authState.value.loggedIn) {
        if (!relatedResearchDocuments.value) {
            allSearchResults.push({
                title: 'Research',
                optionHolder: { options: [], capped: false },
                showId: false,
                resultCount: 0,
                showOwner: false,
                blockClickingLockedItems: true,
            });
        } else {
            allSearchResults.push(getResearchResults());
        }
    }

    allSearchResults.push(getPageResults());
    allSearchResults.push(...getPlantResults());
    allSearchResults.push(...getStoretrackResults());

    if (
        !userAccessStore.userAccess.research.isSubscriber
        && authState.value.loggedIn
        && relatedResearchDocuments.value
    ) {
        allSearchResults.push(getResearchResults());
    }

    const filtered = allSearchResults.filter(category => shouldIncludeSearchCategory(category, fetchingEmbeddedSearchDocumentsResults.value));
    const pinned = new Set(pinnedCategoryIds.value);

    const pinnedResults = filtered.filter(result => pinned.has(result.title));
    const unpinnedResults = filtered.filter(result => !pinned.has(result.title));
    const unpinnedEnactResults = unpinnedResults.filter(result => !isNonEnactSearchCategory(result.title));
    const unpinnedNonEnactResults = unpinnedResults.filter(result => isNonEnactSearchCategory(result.title));

    if (currentRouteIsEnact.value) {
        return [...pinnedResults, ...unpinnedEnactResults, ...unpinnedNonEnactResults];
    }

    return [...pinnedResults, ...unpinnedNonEnactResults, ...unpinnedEnactResults];
});

function isNonEnactSearchCategory (title: string): boolean {
    return title === 'Research' || title === 'Features';
}

function shouldIncludeSearchCategory (
    result: ISearchResults,
    isFetchingResearchResults: boolean,
): boolean {
    if (result.optionHolder.options.length > 0) return true;
    return result.title === 'Research'
        && (isFetchingResearchResults
            || (activeResearchSelection.value !== ResearchSelectionType.AllProducts
                && (relatedResearchDocuments.value?.length ?? 0) > 0));
}

function getResearchResults (): ISearchResults {
    const documents = relatedResearchDocuments.value ?? [];
    let selectedDocuments = documents;
    if (activeResearchSelection.value === ResearchSelectionType.MyProducts) {
        selectedDocuments = documents.filter(doc => isRichContentDocument(doc) || doc.myResearch);
    } else if (activeResearchSelection.value === ResearchSelectionType.PublicProducts) {
        selectedDocuments = documents.filter(doc => doc.accessReason === 'Public');
    }
    const formattedOptions = selectedDocuments.map(formatResearchSearchOption);

    return {
        title: 'Research',
        optionHolder: {
            options: formattedOptions.slice(0, 100),
            capped: formattedOptions.length > 100,
        },
        showId: false,
        resultCount: formattedOptions.length,
        showOwner: false,
    };
}

function getPlantResults (): Array<ISearchResults> {
    const allSearchResults: Array<ISearchResults> = [];

    if (dataStore.plantStore) {
        const gbPlants: Array<IPlant> = [];
        const allPlants: IPlantCategoriesByType = dataStore.plantStore.allPlants;

        Object.keys(allPlants).forEach((key: string) => {
            if (key === 'Owner') return;
            gbPlants.push(...allPlants[key as keyof IPlantCategoriesByType].plants);
        });

        const gbOptions = createOptions('GB Plants', gbPlants);
        allSearchResults.push(gbOptions);

        if (searchDefinitions.value) {
            const glossaryOptions = createOptions('Glossary');
            allSearchResults.push(glossaryOptions);
        }

        const plantsByEndpoint = dataStore.plantsByEndpoint;
        if (Object.keys(plantsByEndpoint).length) {
            Object.keys(plantsByEndpoint).forEach(endpointId => {
                if (endpointId === 'gb') return;
                const plants: Array<IPlant> = [];
                if (typeof plantsByEndpoint[endpointId] === 'object') {
                    Object.keys(plantsByEndpoint[endpointId].plantList).forEach((countryId) => {
                        plants.push(...dataStore.allPlantsByCountry[countryId]);
                    });
                    const displayName = enactApiBaseKeyValues[endpointId as keyof typeof enactApiBaseKeyValues] || endpointId;
                    const endpointOptions = createOptions(`${displayName} Plants`, plants);
                    if (endpointOptions === null) return;
                    allSearchResults.push(endpointOptions);
                }
            });
        }
    }

    return allSearchResults;
}

const resultsToOptions = computed<Record<string, Array<string>>>(() => {
    const options: Record<string, Array<string>> = {};

    results.value.forEach(result => {
        const ids = result.optionHolder.options.map(option => option.id);

        const pinned = ids.filter(id => pinnedSearchIds.value.includes(id));
        const unpinned = ids.filter(id => !pinnedSearchIds.value.includes(id));

        options[result.title] = [...pinned, ...unpinned];
    });

    return options;
});

function createOptions (title: string, options?: Array<IPlant>): ISearchResults {
    if (title === 'Glossary') {
        const optionsList = filterDefinitionOptions(searchDefinitions.value);
        return {
            title,
            optionHolder: optionsList,
            showId: false,
            resultCount: optionsList.options.length,
            showOwner: true,
        };
    }
    const optionsList = filterPlantOptions(options!);
    return {
        title,
        optionHolder: optionsList,
        showId: true,
        resultCount: optionsList.options.length,
        showOwner: true,
        blockClickingLockedItems: true,
    };
}

function getStoretrackResults (): Array<ISearchResults> {
    let filteredCompanyOptions = [...storetrackSearchableContent.value.companies];
    let filteredProjectOptions = [...storetrackSearchableContent.value.projects];

    searchString.value.split(' ').forEach(character => {
        filteredCompanyOptions = filteredCompanyOptions.filter((option: IFormattedSearchOptions) => option.searchQuery.includes(character.toLowerCase()));
        filteredProjectOptions = filteredProjectOptions.filter((option: IFormattedSearchOptions) => option.searchQuery.includes(character.toLowerCase()));
    });

    const companyOptions = {
        options: filteredCompanyOptions.slice(0, 100),
        capped: filteredCompanyOptions.length > 100,
    };

    const projectOptions = {
        options: filteredProjectOptions.slice(0, 100),
        capped: filteredProjectOptions.length > 100,
    };

    const companyResults = {
        title: 'Storage Companies',
        optionHolder: companyOptions,
        showId: false,
        resultCount: companyOptions.options.length,
        showOwner: false,
        blockClickingLockedItems: true,
    };

    const projectResults = {
        title: 'Storage Assets',
        optionHolder: projectOptions,
        showId: false,
        resultCount: projectOptions.options.length,
        showOwner: false,
        useCountryIcon: true,
        blockClickingLockedItems: true,
    };

    return [companyResults, projectResults];
}

function getPageResults (): ISearchResults {
    let filteredOptions = [...searchablePages.value];
    searchString.value.split(' ').forEach(character => {
        filteredOptions = filteredOptions.filter((option: IFormattedSearchOptions) => option.searchQuery.includes(character.toLowerCase()));
    });

    const pageOptions = {
        options: filteredOptions.slice(0, 100),
        capped: filteredOptions.length > 100,
    };

    return {
        title: 'Pages',
        optionHolder: pageOptions,
        showId: false,
        resultCount: pageOptions.options.length,
        showOwner: false,
    };
}

function filterPlantOptions (plants: Array<IPlant>): IFormattedSearchResults {
    let filteredOptions = [...plants];
    searchString.value.split(' ').forEach(character => {
        filteredOptions = filteredOptions.filter((option: IPlant) => option.searchQuery?.toLowerCase().includes(character.toLowerCase()));
    });

    const formattedOptions: Array<IFormattedSearchOptions> = filteredOptions.map((option: IPlant) => ({
        id: option.id,
        title: option.name,
        owner: option.owner ?? null,
        icon: option.icon,
        colour: option.colour,
        searchQuery: option.searchQuery,
        isLocked: !userAccessStore.enactPageAccess.assets.hasAccess,
    })) as Array<IFormattedSearchOptions>;

    return {
        options: formattedOptions.slice(0, 100),
        capped: filteredOptions.length > 100,
    };
}

function filterDefinitionOptions (definitions: Array<IDefinitionSearchInfo>): IFormattedSearchResults {
    let filteredOptions = [...definitions];
    searchString.value.split(' ').forEach(character => {
        filteredOptions = filteredOptions.filter((option: IDefinitionSearchInfo) => option.searchQuery.toLowerCase().includes(character.toLowerCase()));
    });
    const formattedOptions: Array<IFormattedSearchOptions> = filteredOptions.map((option: IDefinitionSearchInfo) => ({
        id: option.id,
        title: option.title,
        owner: null,
        icon: option.icon,
        colour: option.colour,
        searchQuery: option.searchQuery,
        isLocked: option.hasAccess === false,
    }));

    return {
        options: formattedOptions.slice(0, 100),
        capped: filteredOptions.length > 100,
    };
}

// #endregion

// #region key navigation

function focusSearchInput (): void {
    const component = searchInput.value;
    if (!component) return;
    const root = (component as { $el?: HTMLElement }).$el;
    const input = root instanceof HTMLInputElement
        ? root
        : root?.querySelector?.('input');
    (input as HTMLInputElement | undefined)?.focus();
}

function findIdleSearchByFocusedId (): IRecentSearch | undefined {
    return navigableIdleSearches.value.find(item => item.option.id === focusedElement.value);
}

function focusFirstSearchResult (): void {
    if (searchString.value.length < 2) {
        const firstId = navigableIdleSearches.value[0]?.option.id || '';
        focusedElement.value = firstId;
        if (firstId) {
            nextTick(() => {
                focusSearchInput();
                scrollElementIntoView();
            });
        }
        return;
    }
    focusedElement.value = resultsToOptions.value[activeTab.value][0] || '';
}

function handleKeydown (e: KeyboardEvent): void {
    if (e.key === 'Escape') {
        e.preventDefault();
        emit('close');
        emit('update:isFocused', false);
        return;
    }

    if (e.target instanceof HTMLElement && e.target.closest('.research-scope-toggle, .research-view-all-button')) {
        return;
    }

    if (e.key === 'ArrowDown') {
        e.preventDefault();
        if (focusedElement.value === '') {
            focusFirstSearchResult();
            return;
        }

        if (focusedElementInTabs()) {
            navigateThroughTabs('next');
            return;
        }

        if (focusedElementInOptions()) {
            navigateThroughOptions('next');
            return;
        }

        if (focusedElementInRecentSearches()) {
            navigateThroughRecentSearches('next');
            return;
        }
    }

    if (e.key === 'ArrowUp') {
        e.preventDefault();
        if (focusedElement.value === '') {
            focusFirstSearchResult();
            return;
        }

        if (focusedElementInTabs()) {
            navigateThroughTabs('previous');
            return;
        }

        if (focusedElementInOptions()) {
            navigateThroughOptions('previous');
            return;
        }

        if (focusedElementInRecentSearches()) {
            navigateThroughRecentSearches('previous');
            return;
        }
    }

    if (e.key === 'ArrowRight') {
        if (searchString.value.length < 2) {
            return;
        }

        e.preventDefault();
        if (focusedElement.value === '') {
            focusFirstSearchResult();
            return;
        }

        if (focusedElementInTabs()) {
            const options = resultsToOptions.value[activeTab.value];
            if (options.includes(previouslyFocusedElement.value)) {
                focusedElement.value = previouslyFocusedElement.value;
                scrollElementIntoView();
                return;
            }

            focusFirstSearchResult();
            scrollElementIntoView();
            return;
        }
    }

    if (e.key === 'ArrowLeft') {
        if (searchString.value.length < 2) {
            return;
        }

        e.preventDefault();

        if (focusedElement.value === '') {
            focusedElement.value = Object.keys(resultsToOptions.value)[0];
            return;
        }

        if (focusedElementInOptions()) {
            previouslyFocusedElement.value = focusedElement.value;
            focusedElement.value = activeTab.value;
            return;
        }
    }

    if (e.key === 'Enter') {
        e.preventDefault();
        if (focusedElementInTabs()) {
            activeTab.value = focusedElement.value;
            focusedElement.value = resultsToOptions.value[focusedElement.value][0];
            previouslyFocusedElement.value = focusedElement.value;
            return;
        }

        if (focusedElementInOptions()) {
            const selectedOption = results.value.find(result => result.title === activeTab.value)?.optionHolder.options
                .find(option => option.id === focusedElement.value);
            if (selectedOption) {
                const currentResult = results.value.find(result => result.title === activeTab.value);
                if (currentResult?.blockClickingLockedItems && selectedOption.isLocked) return;
                selectSearchResult(activeTab.value, selectedOption, currentResult?.useCountryIcon ?? false);
            }
            return;
        }

        if (focusedElementInRecentSearches()) {
            const idleSearch = findIdleSearchByFocusedId();
            if (idleSearch) {
                selectRecentSearch(idleSearch);
            }
        }
    }
}

function selectSearchResult (resultTitle: string, option: IFormattedSearchOptions, useCountryIcon: boolean): void {
    searchStore.addRecentSearch(option, resultTitle, useCountryIcon);
    emit('select', resultTitle, option.id);
}

function focusedElementInTabs (): boolean {
    if (searchString.value.length < 2) return false;
    return Object.keys(resultsToOptions.value).includes(focusedElement.value);
}

function focusedElementInOptions (): boolean {
    if (searchString.value.length < 2) return false;
    return resultsToOptions.value[activeTab.value].includes(focusedElement.value);
}

function focusedElementInRecentSearches (): boolean {
    return searchString.value.length < 2 && focusedElement.value !== ''
        && navigableIdleSearches.value.some(item => item.option.id === focusedElement.value);
}

function navigateThroughOptions (direction: 'next' | 'previous'): void {
    const options = resultsToOptions.value[activeTab.value];
    const currentIndex = options.indexOf(focusedElement.value);
    const nextIndex = direction === 'next' ? currentIndex + 1 : currentIndex - 1;
    if (nextIndex >= 0 && nextIndex < options.length) {
        focusedElement.value = options[nextIndex];
        scrollElementIntoView();
    }
}

function navigateThroughTabs (direction: 'next' | 'previous'): void {
    const resultKeys = Object.keys(resultsToOptions.value);
    const currentIndex = resultKeys.indexOf(focusedElement.value);
    const nextIndex = direction === 'next' ? currentIndex + 1 : currentIndex - 1;
    if (nextIndex >= 0 && nextIndex < resultKeys.length) {
        focusedElement.value = resultKeys[nextIndex];
        scrollElementIntoView();
    }
}

function navigateThroughRecentSearches (direction: 'next' | 'previous'): void {
    const list = navigableIdleSearches.value;
    const currentIndex = list.findIndex(item => item.option.id === focusedElement.value);
    const nextIndex = direction === 'next' ? currentIndex + 1 : currentIndex - 1;
    if (direction === 'previous' && nextIndex < 0 && currentIndex === 0) {
        focusedElement.value = '';
        focusSearchInput();
        return;
    }
    if (nextIndex >= 0 && nextIndex < list.length) {
        focusedElement.value = list[nextIndex].option.id;
        nextTick(() => {
            focusSearchInput();
            scrollElementIntoView();
        });
        return;
    }
    scrollElementIntoView();
}

function scrollElementIntoView (): void {
    const element = document.querySelector<HTMLElement>('.focused-element');
    if (!element) return;
    if (results.value.length > 0 && searchString.value.length > 0) {
        if (!resultsScrollContainer.value) return;
        const container = resultsScrollContainer.value.find(containerEl => containerEl.contains(element));
        if (!container) return;
        scrollIntoViewWithinContainer(element, container, { behavior: 'smooth', block: 'center' });
    } else {
        let container: HTMLElement | null = null;
        if (pinnedSearchScrollContainer.value?.contains(element)) {
            container = pinnedSearchScrollContainer.value;
        } else if (recentSearchScrollContainer.value?.contains(element)) {
            container = recentSearchScrollContainer.value;
        }
        if (container) {
            scrollIntoViewWithinContainer(element, container, { behavior: 'smooth', block: 'center' });
        }
    }
}

type ScrollOpts = { behavior?: ScrollBehavior; block?: 'center' | 'nearest' | 'start' | 'end' };

function scrollIntoViewWithinContainer (
    el: HTMLElement,
    container: HTMLElement,
    opts: ScrollOpts = { behavior: 'auto', block: 'nearest' },
): void {
    const elRect = el.getBoundingClientRect();
    const contRect = container.getBoundingClientRect();

    // element pos relative to container content top:
    const offsetTop = elRect.top - contRect.top + container.scrollTop;
    const offsetBottom = offsetTop + elRect.height;

    if (opts.block === 'center') {
        const targetScroll = offsetTop - (container.clientHeight / 2) + (elRect.height / 2);
        container.scrollTo({ top: targetScroll, behavior: opts.behavior ?? 'auto' });
        return;
    }

    // 'nearest' behaviour: scroll only when element is out of view
    if (offsetTop < container.scrollTop) {
    // element above view
        container.scrollTo({ top: offsetTop, behavior: opts.behavior ?? 'auto' });
    } else if (offsetBottom > container.scrollTop + container.clientHeight) {
    // element below view
        const target = offsetBottom - container.clientHeight;
        container.scrollTo({ top: target, behavior: opts.behavior ?? 'auto' });
    }
}

// #endregion

async function getEmbeddedSearchDocumentsResults (): Promise<void> {
    const relatedDocuments = await deltaGptStore.getEmbeddedSearchResults(searchString.value);
    relatedResearchDocuments.value = relatedDocuments
        .filter(doc => isResearchBrowseDocument(doc) || isRichContentDocument(doc));

    setActiveTabToFirstTab();
    fetchingEmbeddedSearchDocumentsResults.value = false;
}

function formatResearchSearchOption (doc: IDocumentSimple): IFormattedSearchOptions {
    const richContentIcon = isRichContentDocument(doc) ? getRichContentSearchIcon(doc) : null;

    return {
        id: doc.documentShortCode,
        title: doc.documentTitle,
        searchQuery: '',
        icon: richContentIcon?.icon ?? ['fas', 'chart-line'],
        extraInfo: formatPublicationDateForDisplay(doc.publicationDate),
        isLocked: !doc.hasAccess,
        colour: richContentIcon?.colour ?? 'var(--accentColour)',
    };
}

function getRichContentSearchIcon (doc: IDocumentSimple): { icon: Array<string>; colour?: string } {
    const logo = getReportTypeMetadata(doc.reportType).logo;
    if (logo?.kind !== 'icon') {
        return { icon: ['fas', 'newspaper'], colour: 'var(--accentColour)' };
    }

    return {
        icon: [logo.iconFamily, logo.source],
        colour: logo.colour ?? 'var(--accentColour)',
    };
}

function setActiveTabToFirstTab (): void {
    if (!activeTab.value) {
        activeTab.value = Object.keys(resultsToOptions.value)?.[0] || '';
        focusedElement.value = '';
    }
}

async function initialise (): Promise<void> {
    if (!dataStore.plantStore) await dataStoreActions.getPlantStore();
    if (!definitionStore.searchInfo.length) {
        await definitionStore.fetchAllDefinitionSearchInfo();
    }
    searchDefinitions.value = definitionStore.searchInfo;
    if (!activeTab.value && results.value.length > 0) {
        activeTab.value = results.value[0].title; // do we want to do some sort of search preference saving?
    }
    if (!deltaGptStore.allDocumentsSimple.length) {
        await deltaGptStore.fetchAllDocumentInfo();
    }
}

initialise();

onMounted(() => {
    window.addEventListener('keydown', handleKeydown);
});

onBeforeUnmount(() => {
    window.removeEventListener('keydown', handleKeydown);
});

watch(results, (newValue, oldValue) => {
    if ((newValue.length === 0 && oldValue.length > 0) || (newValue.length > 0 && oldValue.length === 0)) {
        focusedElement.value = '';
        return;
    }

    if (!Object.keys(resultsToOptions.value).includes(activeTab.value)) {
        activeTab.value = '';
        const options = resultsToOptions.value[activeTab.value];
        if (options?.includes(focusedElement.value)) {
            focusedElement.value = resultsToOptions.value[activeTab.value].length > 0
                ? resultsToOptions.value[activeTab.value][0]
                : '';

            scrollElementIntoView();
        }
    }

    setActiveTabToFirstTab();
});

watch(searchString, () => {
    fetchingEmbeddedSearchDocumentsResults.value = true;
});

watchDebounced(searchString, async () => {
    await getEmbeddedSearchDocumentsResults();
}, { debounce: 1000 });

</script>

<style lang="scss" scoped>
.pin-icon {
    color: white;
    opacity: 0;
    margin-left: auto;
    padding: var(--defaultSpacing);
    display: inline-block;
}

.recent-searches-item:hover :deep(.pin-icon) {
  opacity: 1;
}

.search-result-option:hover :deep(.pin-icon) {
  opacity: 1;
}

.search-results-left-column-button:hover :deep(.pin-icon) {
  opacity: 1;
}

.pin-icon.pinned {
    color: var(--lcpGrapefruit);
    transform: rotate(20deg);
    opacity: 1;
}

.recent-searches {
    background-color: var(--neutral0);
    display: flex;
    flex-direction: column;
    margin-top: var(--largeSpacing);
    flex: 1;
    min-height: 0;

    &-title {
        padding: var(--defaultSpacing) var(--largeSpacing);
        font-weight: bold;
    }

    &-item {
        button {
            width: 100%;
            height: 100%;
            padding: var(--largeSpacing);
            text-align: left;
            display: grid;
            grid-template-columns: auto 4rem 1fr auto;
            gap: var(--largeSpacing);
            align-items: center;
            font-size: var(--smallFontSize);

             > :first-child {
                justify-self: center;
                align-self: center;
            }

            .fi {
                width: 0;
            }

            &:hover {
                background-color: var(--neutral300);
            }

            .recent-search-tab {
                color: var(--neutral600);
                margin-left: auto;
                font-style: italic;
            }
        }
    }
}

.locked-icon {
    text-align: right;
}

.search-result-option-icon {
    font-size: var(--extraLargeFontSize);
}

.search-result-option-country-flag {
    font-size: var(--extraLargeFontSize);
    min-width: var(--extraLargeFontSize);
}

.search-result-locked-icon {
    font-size: var(--largeFontSize);
    color: var(--accentColour);
}

.search-result-extra-info {
    color: var(--neutral600);
}

.no-results {
    padding: var(--largeSpacing);
    background-color: var(--neutral0);
}

.fade-slide-enter-from,
.fade-slide-leave-to {
    opacity: 0;
    transform: translateY(-10px); /* optional slide */
}

.fade-slide-enter-to,
.fade-slide-leave-from {
    opacity: 1;
    transform: translateY(0);
}

.fade-slide-enter-active,
.fade-slide-leave-active {
    transition: opacity 300ms ease, transform 300ms ease;
}

.embedded-inner {
    position: absolute;
    width: 100%;
    top: 0;
    max-height: 60vh;
    border: transparent 1px solid;
    z-index: 10;

    .back-button {
        display: none;
    }

    &:focus-within {
        background-color: var(--neutral0);
        border-color: var(--neutral100);
        box-shadow: var(--shadow3);
    }
}

.modal-inner {
    position: relative;
    width: 100%;
    height: 100%;
    max-height: 100%;
    min-height: 0;
    background-color: transparent;
}

.search-modal-inner {
    border-radius: var(--borderRadiusOuter);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    padding: var(--extraLargeSpacing);
    gap: var(--smallSpacing);
    box-sizing: border-box;

    .back-button {
        margin-right: auto;
    }

    @include responsive(sm) {
        padding: var(--defaultSpacing);
        border-radius: 0;
    }

    .search-input {
        width: 100%;
        padding: var(--defaultSpacing) var(--largeSpacing);
        border: var(--neutral500) 2px solid;
        border-radius: var(--borderRadiusOuter);
        background-color: var(--neutral0);

        @include responsive(sm) {
            padding: 0;
        }

        > * {
            background-color: inherit;
            border: none;
        }

        &:has(input:focus) {
            border-color: var(--neutral600);
        }

        .p-inputgroupaddon {
            font-size: var(--extraLargeFontSize);
        }
    }

    .search-info {
        padding-top: var(--extraLargeSpacing);
        overflow: hidden;
        display: grid;
        grid-template-rows: auto auto auto;

        @include responsive(sm) {
            padding: var(--defaultSpacing);
        }

        .recent-search-container {
            overflow-y: auto;
            height: 100%;
        }
    }

    .search-results {
        padding-top: var(--largeSpacing);
        display: grid;
        grid-template-columns: auto 1fr;
        overflow-y: hidden;
        min-height: 0;
        gap: var(--smallSpacing);

        &-left-column {
            display: flex;
            flex-direction: column;
            overflow-y: auto;

            .result-count {
                color: var(--accentColour);
                margin-left: var(--smallSpacing);
            }

            &-button {
                text-align: left;
                height: 6rem;
                padding: 0 var(--largeSpacing);
                background-color: var(--neutral0);
                display: flex;
                flex-direction: row;
                align-items: center;
                gap: var(--smallSpacing);

                &:hover {
                    background-color: var(--neutral300);
                }

                &-active {
                    background-color: var(--neutral100);
                    border-left: var(--accentColour) 2px solid;
                }
            }
        }

        &-right-column {
            display: flex;
            flex-direction: column;
            overflow-y: hidden;
            min-height: 0;

            &-inner {
                flex: 1;
                min-height: 0;
                overflow-y: auto;
                display: flex;
                flex-direction: column;

                .loading-results {
                    width: 100%;
                    height: 100%;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                }

                .search-result-option {
                    align-items: center;
                    text-align: left;
                    cursor: pointer;
                    height: 6rem;

                    td {
                        padding: var(--defaultSpacing);
                        text-wrap: wrap;
                        border: none;
                        background-color: var(--neutral0);

                        &:first-child {
                            width: 4rem;
                            text-align: center;
                        }
                    }

                    &:hover td {
                        background-color: var(--neutral300);
                    }
                }

                .search-result-blocked {
                    cursor: auto;
                }
                .research-results-toolbar {
                    position: sticky;
                    top: 0;
                    z-index: 1;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    flex-wrap: wrap;
                    gap: var(--largeSpacing);
                    min-height: 6rem;
                    padding: var(--defaultSpacing) var(--largeSpacing);
                    background-color: var(--neutral0);

                    .view-all-documents-icon {
                        transform: translateX(0);
                        transition: all 0.5s ease;
                    }
                }
            }
        }
    }
}

.focused-element, .focused-element td {
    background-color: var(--neutral300)!important;
}

</style>
